import { useState, useCallback, useEffect, useRef } from "react";
import { useWindowManager } from "../../context/WindowManagerContext";
import { APP_REGISTRY } from "../../lib/appRegistry";
import {
  getDesktopIconIds,
  removeFromDesktop,
  isOnDesktop,
} from "../../lib/desktopIcons";

interface CtxMenu {
  id: string;
  x: number;
  y: number;
}

export function DesktopIconGrid() {
  const { openWindow } = useWindowManager();
  const [iconIds, setIconIds] = useState<string[]>(getDesktopIconIds);
  const [ctx, setCtx] = useState<CtxMenu | null>(null);
  const ctxRef = useRef<HTMLDivElement>(null);

  // Sync when localStorage changes (e.g. add from Start Menu)
  useEffect(() => {
    const onStorage = () => setIconIds(getDesktopIconIds());
    window.addEventListener("storage", onStorage);
    window.addEventListener("opentool-desktop-icons-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("opentool-desktop-icons-changed", onStorage);
    };
  }, []);

  // Close ctx menu on outside click
  useEffect(() => {
    if (!ctx) return;
    const close = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) {
        setCtx(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [ctx]);

  const handleOpen = useCallback(
    (id: string) => {
      openWindow(id);
      window.location.hash = id;
    },
    [openWindow],
  );

  const handleRemove = useCallback((id: string) => {
    removeFromDesktop(id);
    setIconIds(getDesktopIconIds());
    window.dispatchEvent(new CustomEvent("opentool-desktop-icons-changed"));
    setCtx(null);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setCtx({ id, x: e.clientX, y: e.clientY });
  }, []);

  const icons = iconIds
    .map((id) => APP_REGISTRY.find((a) => a.id === id))
    .filter(Boolean) as typeof APP_REGISTRY[number][];

  return (
    <>
      <div className="wm-desktop-icons">
        {icons.map((app) => (
          <button
            key={app.id}
            className="wm-desktop-icon"
            onClick={() => handleOpen(app.id)}
            onContextMenu={(e) => handleContextMenu(e, app.id)}
          >
            <div className="wm-desktop-icon__img">
              <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d={app.icon} />
              </svg>
            </div>
            {app.label}
          </button>
        ))}
      </div>

      {ctx && (
        <div
          ref={ctxRef}
          className="wm-ctx"
          style={{ position: "fixed", left: ctx.x, top: ctx.y, zIndex: 9999 }}
        >
          <button
            className="wm-ctx__item"
            onClick={() => { handleOpen(ctx.id); setCtx(null); }}
          >
            Open
          </button>
          {isOnDesktop(ctx.id) && (
            <button
              className="wm-ctx__item wm-ctx__item--danger"
              onClick={() => handleRemove(ctx.id)}
            >
              Remove from Desktop
            </button>
          )}
        </div>
      )}
    </>
  );
}
